import getQualifiedImageUrl from '@utils/imageUrlProcessor';
import {
  getPostImage,
  getPostIndicatorIcon,
  getPostLink,
} from '@utils/post.helper';
import PostType from 'constants/postType.constants';
import { cx } from 'cva';
import { isArray, isEmpty, size } from 'lodash-es';
import Image from 'next/image';
import React, { useEffect, useState } from 'react';
import Avatar from '@ui/Avatar';
import Link from '@ui/Link';
import PostImagesLinks from './PostImagesLinks';
import { handleOpenPost } from '@/actions/server.actions';
import useHasUserSession from '@/hooks/useHasUserSession';

function PostImageLink({ post, className = '' }) {
  const hasUserSession = useHasUserSession();
  const [aspectRatio, setAspectRatio] = useState(null);
  const imageTitle =
    post.renderProps.title?.length > 15
      ? post.renderProps.title.slice(0, 15)
      : post.renderProps.title || null;

  if (post.sourcePostType === PostType.NewsFeeds) return null;

  const indicatorIcon = getPostIndicatorIcon(post);
  const hasMultipleImages =
    isArray(post.renderProps.imageUrls) && size(post.renderProps.imageUrls) > 1;
  const image = getPostImage(post);
  const postLink = getPostLink(post);

  useEffect(() => {
    if (image?.url && image.type !== 'logo') {
      const img = new window.Image();
      img.src = getQualifiedImageUrl(image.url);
      img.onload = () => {
        if (img.width && img.height) {
          setAspectRatio(img.height / img.width);
        }
      };
    }
  }, [image?.url]);

  const renderSinglePostImage = () => {
    if (
      isEmpty(post?.renderProps?.imageUrls) &&
      post?.sourcePostType === 'UserPost' &&
      post?.renderProps?.nonProfit?.logo_url
    ) {
      return (
        <Avatar
          src={getQualifiedImageUrl(post.renderProps.nonProfit.logo_url)}
          alt={imageTitle || 'nonprofit-logo'}
          size="2xl"
          className="object-contain"
        />
      );
    }

    if (image.type === 'logo') {
      return (
        <Avatar
          src={getQualifiedImageUrl(image.url)}
          alt={imageTitle || 'Post Image'}
          size="2xl"
          className="object-contain"
        />
      );
    }

    return (
      <Image
        src={getQualifiedImageUrl(image.url)}
        alt={imageTitle || 'Post Image'}
        fill
        className="bg-image-background object-contain"
        sizes="(max-width: 768px) 100vw,
                (max-width: 1200px) 50vw,
                33vw"
      />
    );
  };

  if (
    isEmpty(post?.renderProps?.imageUrls) &&
    post?.sourcePostType === 'UserPost'
  ) {
    return null;
  }

  const containerStyles =
    !hasMultipleImages && image.type !== 'logo' && aspectRatio
      ? { position: 'relative', paddingTop: `${aspectRatio * 100}%` }
      : { position: 'relative', height: '100%' };

  const handleLogPostRedirect = () => {
    if (hasUserSession) {
      handleOpenPost(post.id);
    }
  };

  return (
    <div
      className={cx(
        'relative w-full overflow-hidden',
        image.type === 'logo' && 'rounded border border-body-background',
        className,
      )}
      style={containerStyles}>
      {hasMultipleImages ? (
        <PostImagesLinks
          onClick={handleLogPostRedirect}
          postLink={postLink}
          imageUrls={post.renderProps.imageUrls}
          message={imageTitle}
        />
      ) : (
        <Link href={postLink} onClick={handleLogPostRedirect}>
          {renderSinglePostImage()}
        </Link>
      )}

      {indicatorIcon && (
        <div className="absolute right-2 top-2 flex h-12 w-12 items-center justify-center rounded-full border border-gray-100 bg-white p-3 lg:h-[72px] lg:w-[72px]">
          <img
            src={indicatorIcon}
            alt="Post Indicator Icon"
            className="h-8 w-8 lg:h-16 lg:w-16"
          />
        </div>
      )}
    </div>
  );
}

export default PostImageLink;
